/**
 * A glyph atlas, rasterised by the browser and uploaded once.
 *
 * Ported from `ObenorUI/src/core/font.ts`, which is the same idea already
 * working in another WebGPU project of this author's: measure every glyph on a
 * 2D canvas, pack them into a near-square power-of-two sheet, and keep a uv
 * rect and an advance for each. What it buys over the HUD's canvas overlay is
 * not sharpness — native canvas text is already sharp — it is SCALE: any
 * number of labels in one draw call, positioned by a matrix rather than by a
 * separate 2D pass, and strings that do not have to be known in advance.
 *
 * The other route this project has used is in `HelicopterGame`: rasterise a
 * whole STRING to a canvas and map it onto a quad. That is the better answer
 * for a fixed set of phrases — no atlas, no layout, perfect shaping — and it
 * is why the banners there are done that way. It stops being the answer the
 * moment the text is unbounded, which is what a player name is.
 *
 * ── what this file deliberately does differently from the original ──────────
 *
 * THE ATLAS IS A COVERAGE MASK. Only alpha carries information; every texel's
 * RGB is forced to white after rasterising, including the fully transparent
 * ones. Canvas leaves transparent texels at (0,0,0,0), and a bilinear tap
 * across the edge of a glyph then mixes toward BLACK — which is a dark fringe
 * on every letter, worst exactly where the text is smallest. Flooding the
 * colour and sampling only `.a` removes the failure rather than hiding it, and
 * it is also what lets one atlas serve every colour the game wants to tint
 * text with.
 *
 * THE CHARACTER SET IS EXPLICIT. The original runs 32..127 and silently drops
 * anything else; the ore callouts are built with a MIDDLE DOT, U+00B7, which
 * is outside that range. A missing glyph is not an error anywhere in the
 * pipeline — it simply does not draw — so the set is a declared string and
 * `scripts/check-font.mjs` asserts every label the game can produce is inside
 * it.
 */

/**
 * Rendered at four times the size it is displayed at.
 *
 * Not a quality setting: it is what makes the sheet survive being magnified.
 * A world-space label walks toward the camera and a browser at 200% zoom asks
 * for the same thing, and a 12 px rasterisation has nothing left to give
 * either. Four costs sixteen times the atlas area and the atlas is one small
 * texture, so it is the cheapest quality in the renderer.
 */
export const FONT_RENDER_SCALE = 4;
/** Display size the metrics are normalised to. */
export const FONT_SIZE = 12;
/**
 * MUST MATCH `MONO` in `hud/menu.ts`, and is written out rather than imported.
 *
 * `menu.ts` pulls in World, the config, the save system and the level, so
 * importing one string from it would drag the entire game graph into the
 * renderer — and `world.ts` imports the renderer, so it would also be a cycle.
 * `check-font.mjs` compares the two strings instead.
 */
export const FONT_FAMILY = 'ui-monospace, "SF Mono", Menlo, monospace';
/** Weight, matching `drawLabel`'s. */
export const FONT_WEIGHT = 500;
/** Transparent margin around each glyph, so a bilinear tap cannot reach its neighbour. */
const PADDING = 2 * FONT_RENDER_SCALE;

/**
 * Every character the atlas holds.
 *
 * Printable ASCII, then the handful of typographic marks this game actually
 * writes. The middle dot is the separator in every ore callout; the rest are
 * cheap and are the ones a HUD reaches for next.
 */
export const FONT_CHARS =
  ' !"#$%&\'()*+,-./0123456789:;<=>?@'
  + 'ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`'
  + 'abcdefghijklmnopqrstuvwxyz{|}~'
  + '·°×±–—…↑↓';

export interface GlyphMetrics {
  /** Atlas uv rect. */
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  /** Cell width in DISPLAY pixels, including the padding either side. */
  width: number;
  /** How far the cursor moves after this glyph, in display pixels. */
  advance: number;
  /** Left bearing of the ink inside its cell, in display pixels. */
  xOffset: number;
}

export interface FontAtlas {
  texture: GPUTexture;
  /**
   * The same glyphs as a SIGNED DISTANCE FIELD rather than a coverage mask.
   *
   * A coverage texel says "this pixel is 60% ink", and 60% of a pixel is not a
   * thing you can interpolate — halfway between two coverage texels is not the
   * coverage of the halfway pixel, which is precisely why a bitmap atlas
   * shimmers when it is resampled. A distance texel says "the outline is 3.2
   * texels that way", and THAT interpolates exactly, because distance to a
   * line is linear. The fragment shader then derives coverage from the field
   * and its own screen-space gradient, so the answer is right at any size
   * instead of right at one size.
   *
   * Both are built and both stay resident — about a megabyte each — so the
   * toggle is instant and the two can be compared on the same frame.
   */
  sdf: GPUTexture;
  /**
   * A uv rect that is solid opaque white, so ONE pipeline can draw everything.
   *
   * A callout is glyphs, a backing plate and a leader line. Without this the
   * plate and the line need either a second pipeline or the 2D canvas — and
   * the canvas is a different layer, so the line would sit on top of text it
   * is supposed to point away from. A solid block in the corner of the atlas
   * makes a plate a quad with these uvs and a line a thin quad with these uvs,
   * and the whole callout is one draw.
   */
  solid: { u: number; v: number };
  /**
   * A cell whose alpha ramps 0 -> 1 -> 0 across its width, for soft-edged
   * lines.
   *
   * The text pass draws straight onto the swapchain after every antialiasing
   * stage in the frame — it has to, because a glyph through TAA reprojects
   * against a motion vector it never wrote — so nothing downstream will smooth
   * a hard quad edge. A one-pixel leader line then covers a pixel centre on
   * some frames and misses on others, which is a line that blinks.
   *
   * MSAA would fix it and costs a full-screen four-sample target for a few
   * dozen quads. This costs one cell: map the ramp across the line's width and
   * the edge is analytically soft, at any width, with no extra pass.
   */
  ramp: { u0: number; v0: number; u1: number; v1: number };
  sampler: GPUSampler;
  /** Full cell height — ascent + descent + padding — in display pixels. */
  glyphHeight: number;
  ascent: number;
  descent: number;
  /** Top of the cell to the baseline, in display pixels. */
  baselineOffset: number;
  atlasWidth: number;
  atlasHeight: number;
  glyphs: Map<number, GlyphMetrics>;
}

/**
 * Felzenszwalb's exact Euclidean distance transform, one dimension.
 *
 * `f` is a cost per sample — 0 where the feature is, a large number where it
 * is not — and the result is the SQUARED distance to the nearest feature. The
 * two-dimensional transform is this along every row and then along every
 * column of the result, which is the whole reason to use this one: a
 * separable exact EDT is O(n) per pass, where the obvious nested-loop version
 * is O(n^2) and would take minutes on an atlas this size.
 *
 * The algorithm is a lower envelope of parabolas. Each sample q contributes a
 * parabola rooted at q with height f[q]; the transform is the lowest of them
 * at every point, and the loop tracks the vertices `v` and the intersections
 * `z` where the winning parabola changes.
 *
 * Exported because it is the one part of the SDF that can be checked without a
 * GPU or a DOM, and a wrong distance field does not throw — it renders as text
 * that is subtly blurry or subtly blocky, which is unfalsifiable by eye.
 */
export function edt1d(f: Float64Array, n: number, out: Float64Array,
                      v: Int32Array, z: Float64Array) {
  let k = 0;
  v[0] = 0;
  z[0] = -Infinity;
  z[1] = Infinity;
  for (let q = 1; q < n; q++) {
    let s = ((f[q]! + q * q) - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!);
    while (s <= z[k]!) {
      k--;
      s = ((f[q]! + q * q) - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!);
    }
    k++;
    v[k] = q;
    z[k] = s;
    z[k + 1] = Infinity;
  }
  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1]! < q) k++;
    const d = q - v[k]!;
    out[q] = d * d + f[v[k]!]!;
  }
}

/**
 * Squared distance to the nearest set pixel, for a whole grid.
 *
 * `inside(i)` decides what counts as set. Rows first, then columns over the
 * row result — which is what makes the two-dimensional transform exact rather
 * than the chamfer approximation most SDF generators settle for.
 */
export function edt2d(w: number, h: number, inside: (i: number) => boolean): Float64Array {
  const INF = 1e20;
  const grid = new Float64Array(w * h);
  for (let i = 0; i < w * h; i++) grid[i] = inside(i) ? 0 : INF;
  const n = Math.max(w, h);
  const f = new Float64Array(n), out = new Float64Array(n);
  const v = new Int32Array(n), z = new Float64Array(n + 1);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) f[x] = grid[y * w + x]!;
    edt1d(f, w, out, v, z);
    for (let x = 0; x < w; x++) grid[y * w + x] = out[x]!;
  }
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = grid[y * w + x]!;
    edt1d(f, h, out, v, z);
    for (let y = 0; y < h; y++) grid[y * w + x] = out[y]!;
  }
  return grid;
}

/**
 * How far the field reaches, in level-0 texels.
 *
 * It has to cover the widest screen-space gradient the shader will ask for: at
 * 8 texels a glyph minified fourfold still has two texels of field either side
 * of every edge, which is enough for `fwidth` to find a slope. Wider wastes
 * precision — the field is one byte — and narrower clips to flat, which is a
 * hard edge again and the whole problem over.
 */
export const SDF_SPREAD = 8;

function nextPow2(n: number): number {
  return 2 ** Math.ceil(Math.log2(Math.max(1, n)));
}

export function createFontAtlas(device: GPUDevice): FontAtlas {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  const px = FONT_SIZE * FONT_RENDER_SCALE;
  const font = `${FONT_WEIGHT} ${px}px ${FONT_FAMILY}`;
  ctx.font = font;

  // The font's own extremes rather than the em size, measured off a string
  // chosen to reach both — capitals and a bar for the top, descenders for the
  // bottom. `actualBoundingBox*` is what the rasteriser will really produce;
  // the em size is a design value and is routinely smaller than the ink.
  const sample = ctx.measureText('Mgypj|');
  const ascent = sample.actualBoundingBoxAscent ?? px;
  const descent = sample.actualBoundingBoxDescent ?? px * 0.25;
  const lineHeight = Math.ceil(ascent + descent) + PADDING * 2;

  // --- measure ---------------------------------------------------------------
  const chars = [...FONT_CHARS];
  const cells = chars.map((ch) => {
    const m = ctx.measureText(ch);
    return { code: ch.codePointAt(0)!, advance: m.width, w: Math.ceil(m.width) + PADDING * 2 };
  });
  // One more cell, for the solid block. Laid out with the glyphs rather than
  // squeezed into a corner afterwards, so the packer's own arithmetic decides
  // where it goes and there is no way for it to land on top of a letter.
  cells.push({ code: -1, advance: 0, w: lineHeight });
  // And one for the soft ramp.
  cells.push({ code: -2, advance: 0, w: lineHeight });

  // Roughly square, which is what keeps the power-of-two rounding from wasting
  // most of the sheet — a single long row rounds up to the next power of two in
  // width and one line height in the other axis.
  const avgW = cells.reduce((s, c) => s + c.w, 0) / cells.length;
  const perRow = Math.ceil(Math.sqrt((cells.length * lineHeight) / avgW));

  const at: { x: number; y: number }[] = [];
  let cx = 0, cy = 0, maxW = 0;
  for (let i = 0; i < cells.length; i++) {
    if (i > 0 && i % perRow === 0) { maxW = Math.max(maxW, cx); cx = 0; cy += lineHeight; }
    at.push({ x: cx, y: cy });
    cx += cells[i]!.w;
  }
  maxW = Math.max(maxW, cx);

  const atlasWidth = nextPow2(maxW);
  const atlasHeight = nextPow2(cy + lineHeight);
  canvas.width = atlasWidth;
  canvas.height = atlasHeight;

  // --- rasterise -------------------------------------------------------------
  // Resizing a canvas already clears it; `clearRect` says so rather than the
  // original's `fillStyle = 'transparent'`, which is not a colour and fills
  // nothing.
  ctx.clearRect(0, 0, atlasWidth, atlasHeight);
  ctx.font = font;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#fff';

  // The solid block, opaque across its whole cell.
  const solidCell = at[cells.length - 2]!;
  ctx.fillRect(solidCell.x, solidCell.y, lineHeight, lineHeight);

  // The ramp: transparent at both edges, opaque across the middle 60%. A line
  // quad maps this across its width, so the feather is a fixed FRACTION of the
  // line rather than a fixed number of pixels — which is what keeps a thick
  // leader from looking blurred and a thin one from disappearing.
  const rampCell = at[cells.length - 1]!;
  const grad = ctx.createLinearGradient(rampCell.x, 0, rampCell.x + lineHeight, 0);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.2, 'rgba(255,255,255,1)');
  grad.addColorStop(0.8, 'rgba(255,255,255,1)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(rampCell.x, rampCell.y, lineHeight, lineHeight);
  ctx.fillStyle = '#fff';

  const glyphs = new Map<number, GlyphMetrics>();
  for (let i = 0; i < chars.length; i++) {
    const c = cells[i]!, p = at[i]!;
    ctx.fillText(chars[i]!, p.x + PADDING, p.y + PADDING + Math.ceil(ascent));
    glyphs.set(c.code, {
      u0: p.x / atlasWidth,
      v0: p.y / atlasHeight,
      u1: (p.x + c.w) / atlasWidth,
      v1: (p.y + lineHeight) / atlasHeight,
      width: c.w / FONT_RENDER_SCALE,
      advance: c.advance / FONT_RENDER_SCALE,
      xOffset: PADDING / FONT_RENDER_SCALE,
    });
  }

  // --- upload ----------------------------------------------------------------
  const img = ctx.getImageData(0, 0, atlasWidth, atlasHeight);
  // COVERAGE IN ALPHA, COLOUR FLOODED WHITE. See the header: canvas leaves a
  // transparent texel at (0,0,0,0), so a bilinear tap across a glyph's edge
  // mixes toward black and every letter grows a dark fringe. Flooding the
  // colour means the filter only ever interpolates coverage, which is the one
  // channel that has any meaning here.
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) { d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; }

  // MIPS, AND THE ORIGINAL'S REASONING FOR NOT HAVING THEM WAS BACKWARDS.
  //
  // I wrote "text is drawn near its rasterised size or larger, so a mip chain
  // would only be reached when a label has shrunk to nothing". That is exactly
  // wrong for this atlas: it is rasterised at FONT_RENDER_SCALE — 48 px — and
  // displayed at 10 to 14, so it is ALWAYS minified four or five times over. A
  // single bilinear tap at that ratio is a four-texel average of a twenty-texel
  // footprint, and the four it picks change as the label moves sub-pixel. Every
  // stem flickers. Which is what it did.
  //
  // THREE LEVELS AND NO MORE, because a glyph atlas cannot be mipped freely:
  // each level halves the transparent gutter between cells, and once that is
  // gone a letter starts bleeding into its neighbour. PADDING is 8 texels at
  // level 0, so 4 and 2 are still gutters and 1 would not be.
  const MIPS = 3;
  const texture = device.createTexture({
    // No RENDER_ATTACHMENT: nothing ever draws into this, it is written from
    // the CPU and read for the rest of the process.
    size: [atlasWidth, atlasHeight],
    mipLevelCount: MIPS,
    format: 'rgba8unorm',
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
    label: 'font-atlas',
  });
  device.queue.writeTexture(
    { texture }, d, { bytesPerRow: atlasWidth * 4 }, [atlasWidth, atlasHeight],
  );
  // Box-filtered on the CPU. A render pass per level would need a pipeline and
  // a bind group for a texture built once at startup, and a box filter is the
  // correct filter for a power-of-two halving anyway.
  let src = d, sw = atlasWidth, sh = atlasHeight;
  for (let level = 1; level < MIPS; level++) {
    const dw = Math.max(1, sw >> 1), dh = Math.max(1, sh >> 1);
    const dst = new Uint8ClampedArray(dw * dh * 4);
    for (let y = 0; y < dh; y++) {
      for (let x = 0; x < dw; x++) {
        const o = (y * dw + x) * 4;
        // Only alpha is averaged; the colour stays flooded white for the same
        // reason it is flooded at level 0 — a filtered RGB would drag every
        // edge toward black.
        const a0 = src[((y * 2) * sw + x * 2) * 4 + 3]!;
        const a1 = src[((y * 2) * sw + Math.min(x * 2 + 1, sw - 1)) * 4 + 3]!;
        const a2 = src[(Math.min(y * 2 + 1, sh - 1) * sw + x * 2) * 4 + 3]!;
        const a3 = src[(Math.min(y * 2 + 1, sh - 1) * sw + Math.min(x * 2 + 1, sw - 1)) * 4 + 3]!;
        dst[o] = 255; dst[o + 1] = 255; dst[o + 2] = 255;
        dst[o + 3] = (a0 + a1 + a2 + a3) >> 2;
      }
    }
    device.queue.writeTexture(
      { texture, mipLevel: level }, dst, { bytesPerRow: dw * 4 }, [dw, dh],
    );
    src = dst; sw = dw; sh = dh;
  }

  // --- the distance field ----------------------------------------------------
  //
  // Built from the SAME rasterisation, so the two atlases are the same glyphs
  // in the same cells and the toggle changes only how coverage is derived.
  // Inside is coverage over half — the rasteriser's own antialiasing already
  // put the outline there, and thresholding at the midpoint is what recovers
  // it. Two transforms, because a signed field needs the distance out of the
  // ink as well as the distance into it.
  const px2 = atlasWidth * atlasHeight;
  const inside = (i: number) => d[i * 4 + 3]! >= 128;
  // NAMED FOR WHAT THEY MEASURE, because the first version was not and had
  // them exactly backwards. `edt2d(pred)` returns the distance TO the nearest
  // sample where `pred` holds — so passing `inside` gives distance to the ink,
  // which is zero ON the ink and grows away from it. That is the opposite of
  // "distance out of the glyph", which is what a signed field's positive half
  // has to be.
  const toInk = edt2d(atlasWidth, atlasHeight, inside);
  const toVoid = edt2d(atlasWidth, atlasHeight, (i) => !inside(i));
  const sdfData = new Uint8ClampedArray(px2 * 4);
  for (let i = 0; i < px2; i++) {
    // Positive inside, negative outside, in texels. `toVoid` is zero everywhere
    // off the ink and grows inward; `toInk` is its mirror. Subtracting gives
    // one continuous function through the outline — which is the property that
    // makes it interpolable, and the reason a field beats a coverage mask.
    const sd = Math.sqrt(toVoid[i]!) - Math.sqrt(toInk[i]!);
    const o = i * 4;
    sdfData[o] = 255; sdfData[o + 1] = 255; sdfData[o + 2] = 255;
    // 0.5 IS THE OUTLINE, which is the convention the shader's smoothstep
    // depends on. Clamped rather than scaled to fit: a byte spent on the far
    // interior of a stem is a byte not spent near the edge, and near the edge
    // is the only place the value is read.
    sdfData[o + 3] = Math.round(255 * Math.max(0, Math.min(1, 0.5 + sd / (2 * SDF_SPREAD))));
  }
  const sdf = device.createTexture({
    size: [atlasWidth, atlasHeight],
    mipLevelCount: MIPS,
    format: 'rgba8unorm',
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
    label: 'font-sdf',
  });
  device.queue.writeTexture(
    { texture: sdf }, sdfData, { bytesPerRow: atlasWidth * 4 }, [atlasWidth, atlasHeight],
  );
  // Mipped as well. A distance field averages correctly — the mean of two
  // distances IS the distance to the midpoint for a straight edge — which is
  // the same property that lets the shader interpolate it, one level up.
  {
    let src2: Uint8ClampedArray = sdfData, sw2 = atlasWidth, sh2 = atlasHeight;
    for (let level = 1; level < MIPS; level++) {
      const dw = Math.max(1, sw2 >> 1), dh = Math.max(1, sh2 >> 1);
      const dst = new Uint8ClampedArray(dw * dh * 4);
      for (let y = 0; y < dh; y++) {
        for (let x = 0; x < dw; x++) {
          const o = (y * dw + x) * 4;
          const a0 = src2[((y * 2) * sw2 + x * 2) * 4 + 3]!;
          const a1 = src2[((y * 2) * sw2 + Math.min(x * 2 + 1, sw2 - 1)) * 4 + 3]!;
          const a2 = src2[(Math.min(y * 2 + 1, sh2 - 1) * sw2 + x * 2) * 4 + 3]!;
          const a3 = src2[(Math.min(y * 2 + 1, sh2 - 1) * sw2 + Math.min(x * 2 + 1, sw2 - 1)) * 4 + 3]!;
          dst[o] = 255; dst[o + 1] = 255; dst[o + 2] = 255;
          dst[o + 3] = (a0 + a1 + a2 + a3) >> 2;
        }
      }
      device.queue.writeTexture(
        { texture: sdf, mipLevel: level }, dst, { bytesPerRow: dw * 4 }, [dw, dh],
      );
      src2 = dst; sw2 = dw; sh2 = dh;
    }
  }

  return {
    texture,
    sdf,
    // The MIDDLE of the solid cell, not its corner: a uv on the boundary is
    // exactly where a bilinear tap reaches into the neighbouring glyph, and a
    // plate with a letter's edge bleeding into it is a very confusing bug.
    solid: {
      u: (solidCell.x + lineHeight * 0.5) / atlasWidth,
      v: (solidCell.y + lineHeight * 0.5) / atlasHeight,
    },
    // Sampled along the middle row of its cell, so the mip chain's vertical
    // blur cannot pull a neighbour's ink into the ramp.
    ramp: {
      u0: rampCell.x / atlasWidth,
      v0: (rampCell.y + lineHeight * 0.5) / atlasHeight,
      u1: (rampCell.x + lineHeight) / atlasWidth,
      v1: (rampCell.y + lineHeight * 0.5) / atlasHeight,
    },
    // Trilinear. `mipmapFilter` is the half that actually stops the shimmer —
    // without it the sampler snaps between whole levels and a label crossing
    // the boundary pops instead of fading.
    sampler: device.createSampler({
      magFilter: 'linear', minFilter: 'linear', mipmapFilter: 'linear',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge',
      label: 'font-atlas',
    }),
    glyphHeight: lineHeight / FONT_RENDER_SCALE,
    ascent: Math.ceil(ascent) / FONT_RENDER_SCALE,
    descent: Math.ceil(descent) / FONT_RENDER_SCALE,
    baselineOffset: (PADDING + Math.ceil(ascent)) / FONT_RENDER_SCALE,
    atlasWidth,
    atlasHeight,
    glyphs,
  };
}

export function getGlyph(atlas: FontAtlas, code: number): GlyphMetrics | undefined {
  return atlas.glyphs.get(code);
}

/**
 * Advance width of a string at display size, with optional tracking.
 *
 * `tracking` is what the HUD's `drawLabel` calls letter-spacing and applies
 * through the canvas; an atlas has to add it per glyph instead, because the
 * spacing is not baked into an advance the way kerning would be.
 */
export function measureText(atlas: FontAtlas, text: string, tracking = 0): number {
  let w = 0;
  for (const ch of text) {
    const g = atlas.glyphs.get(ch.codePointAt(0)!);
    if (g) w += g.advance + tracking;
  }
  return w;
}

/** Characters in `text` that the atlas has no glyph for. Empty is the good case. */
export function missingGlyphs(text: string): string[] {
  const have = new Set([...FONT_CHARS]);
  return [...new Set([...text])].filter((c) => !have.has(c));
}
